import { db, getDeviceId } from "../db/database";
import type { AppUser, AuthSession } from "../types";
import { apiRequest } from "./api";
import { getNativeAndroidDeviceKey } from "./nativeAndroidConfig";
import { isAndroidApp } from "./platform";

export const ANDROID_USER: AppUser = {
  id: "admin-android",
  name: "Admin Android",
  email: "admin.android@wgtricot.local",
  role: "administrador",
  canImport: true,
  status: "ativo",
};

function sessionFor(user: AppUser, accessToken?: string): AuthSession {
  const authenticatedAt = new Date();
  const offlineLimit = new Date(authenticatedAt);
  offlineLimit.setFullYear(offlineLimit.getFullYear() + (user.id === ANDROID_USER.id ? 10 : 0));
  if (user.id !== ANDROID_USER.id) offlineLimit.setDate(offlineLimit.getDate() + 30);
  return {
    id: "current",
    user,
    accessToken,
    authenticatedAt: authenticatedAt.toISOString(),
    offlineAllowedUntil: offlineLimit.toISOString(),
  };
}

export async function getSession(): Promise<AuthSession | undefined> {
  return db.sessions.get("current");
}

export async function signIn(email: string, password: string): Promise<AuthSession> {
  const settings = await db.settings.get("app");
  if (!settings?.apiUrl) throw new Error("Não foi possível localizar a API do servidor.");
  if (!email.trim() || !password.trim()) throw new Error("Informe e-mail e senha para entrar.");

  const result = await apiRequest<{ user: AppUser; accessToken: string }>("/login", {
    method: "POST",
    body: JSON.stringify({ email, password }),
    anonymous: true,
  });

  const session = sessionFor(result.user, result.accessToken);
  await db.sessions.put(session);
  await db.users.put(result.user);
  return session;
}

export async function signOut(): Promise<void> {
  if (isAndroidApp()) return;
  const session = await getSession();
  if (session?.accessToken) {
    try {
      await apiRequest("/logout", { method: "POST" });
    } catch {
      // O logout local não deve ser bloqueado por falha de rede.
    }
  }
  await db.sessions.delete("current");
}

export async function ensureOfflineSession(): Promise<AuthSession | undefined> {
  const session = await getSession();
  if (!session) return undefined;
  if (Date.parse(session.offlineAllowedUntil) < Date.now() && !navigator.onLine) return undefined;
  return session;
}

async function ensureAndroidSession(options: { failSilently: boolean } = { failSilently: true }): Promise<AuthSession> {
  const saved = await getSession();
  let session = saved?.user.id === ANDROID_USER.id ? saved : sessionFor(ANDROID_USER);
  await db.sessions.put(session);
  await db.users.put(ANDROID_USER);

  const settings = await db.settings.get("app");
  if (!settings?.apiUrl || !navigator.onLine) return session;

  const sevenDays = 7 * 24 * 60 * 60 * 1000;
  if (session.accessToken && Date.now() - Date.parse(session.authenticatedAt) < sevenDays) return session;

  const deviceKey = await getNativeAndroidDeviceKey();
  if (!deviceKey) {
    if (options.failSilently) return session;
    throw new Error("A chave de acesso do aplicativo Android não foi configurada nesta versão.");
  }

  try {
    const result = await apiRequest<{ user: AppUser; accessToken: string }>("/android/session", {
      method: "POST",
      body: JSON.stringify({ deviceKey, deviceId: await getDeviceId(), deviceName: "WG Tricot Estoque Android" }),
      anonymous: true,
    });
    session = sessionFor(result.user, result.accessToken);
    await db.sessions.put(session);
    await db.users.put(result.user);
    return session;
  } catch (error) {
    if (options.failSilently) return session;
    throw error;
  }
}

export async function ensurePlatformSession(): Promise<AuthSession | undefined> {
  return isAndroidApp() ? ensureAndroidSession() : ensureOfflineSession();
}

export async function ensureSyncSession(): Promise<AuthSession | undefined> {
  return isAndroidApp() ? ensureAndroidSession({ failSilently: false }) : getSession();
}
